// ProfileFieldValueUtils.js

// Utilities
import { formatAnnualDateValue } from 'utilities/AnnualDate';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';
import { formatPrice, parseCurrencyInputToCents, resolveFieldCurrencyCode } from 'utilities/Currency';
import {
  getEnumOptionColor,
  getEnumOptionIconKey,
  getEnumOptionLabel,
  resolveEnumOptionsForField,
} from 'utilities/EnumOptions';
import { normalizeEmailValueForStorage } from 'utilities/Email';
import { formatPhoneValueForInput } from 'utilities/Phone';
import { normalizeUrlForOpen } from 'utilities/Url';
import { formatTemporalFieldValue } from 'views/utils/temporalFieldFormat';

export const PROFILE_FIELD_SOURCE = 'member_profile';

export function getProfileFieldValue(profileData, field) {
  return profileData?.[field?.name];
}

export function getProfileFieldType(field) {
  return String(field?.type || '').trim().toLowerCase();
}

export function getProfilePersonKey(person) {
  return String(person?.userKey || person?.key || '').trim();
}

export function getProfilePersonName(person) {
  return String(
    person?.name
    || person?.data_profile?.name
    || person?.title
    || person?.email
    || getProfilePersonKey(person)
    || ''
  ).trim();
}

export function getProfilePeople(value) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const map = new Map();

  list.forEach((entry) => {
    const userKey = getProfilePersonKey(entry);
    if (!userKey) return;

    map.set(userKey, {
      ...entry,
      key: userKey,
      userKey,
      name: getProfilePersonName(entry),
    });
  });

  return Array.from(map.values());
}

export function getProfileEnumOptions(field, language = 'en') {
  return resolveEnumOptionsForField(field, language);
}

export function getProfileEnumValueMeta(field, value, language = 'en') {
  const options = getProfileEnumOptions(field, language);
  const selectedValue = String(value || '').trim();

  if (!selectedValue) return null;

  return {
    color: getEnumOptionColor(options, selectedValue),
    iconKey: getEnumOptionIconKey(options, selectedValue),
    label: getEnumOptionLabel(options, selectedValue, language) || selectedValue,
    value: selectedValue,
  };
}

export function getProfileMultiselectValueMeta(field, value, language = 'en') {
  const options = getProfileEnumOptions(field, language);
  const selectedValues = Array.isArray(value) ? value : value == null || value === '' ? [] : [value];
  const seen = new Set();

  return selectedValues
    .map((entry) => String(entry || '').trim())
    .filter((entry) => {
      if (!entry || seen.has(entry)) return false;
      seen.add(entry);
      return true;
    })
    .map((selectedValue, index) => ({
      color: getEnumOptionColor(options, selectedValue),
      iconKey: getEnumOptionIconKey(options, selectedValue),
      key: `${selectedValue}-${index}`,
      label: getEnumOptionLabel(options, selectedValue, language) || selectedValue,
      value: selectedValue,
    }));
}

export function formatProfileTemporalValue(fieldType, value) {
  if (!value) return '';

  if (fieldType === 'annualdate') {
    return formatAnnualDateValue(value, {
      includeYear: true,
      locale: 'en-US',
    }) || '';
  }

  return formatTemporalFieldValue({
    value,
    fieldType,
    locale: 'en-US',
    dateOptions: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    },
    dateTimeOptions: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
  }) || '';
}

export function formatProfileNumericValue(fieldType, value, field = null) {
  if (value == null || value === '') return '';

  if (fieldType === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (fieldType === 'money') {
    return formatPrice(value, resolveFieldCurrencyCode(field));
  }

  if (fieldType === 'percentage') {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? `${Math.max(0, Math.min(100, Math.round(numericValue)))}%` : '';
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toLocaleString() : String(value);
}

export function formatProfileContactValue(fieldType, value) {
  if (value == null || value === '') return '';

  if (fieldType === 'phone') {
    return formatPhoneValueForInput(value);
  }

  if (fieldType === 'email') {
    return normalizeEmailValueForStorage(value);
  }

  return String(value || '').trim();
}

export function getProfileContactHref(fieldType, value) {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return '';

  if (fieldType === 'phone') {
    return `tel:${normalizedValue}`;
  }

  if (fieldType === 'email') {
    return `mailto:${normalizeEmailValueForStorage(normalizedValue)}`;
  }

  if (fieldType === 'url') {
    return normalizeUrlForOpen(normalizedValue);
  }

  return '';
}

export function normalizeProfileValueForStorage(field, value) {
  const fieldType = getProfileFieldType(field);

  if (fieldType === 'string' || fieldType === 'url') {
    const nextValue = String(value ?? '').trim();
    return nextValue || undefined;
  }

  if (fieldType === 'phone') {
    const nextValue = String(value ?? '').trim();
    return nextValue || undefined;
  }

  if (fieldType === 'email') {
    const nextValue = normalizeEmailValueForStorage(value);
    return nextValue || undefined;
  }

  if (isEnumCompatibleFieldType(fieldType)) {
    const nextValue = String(value ?? '').trim();
    return nextValue || undefined;
  }

  if (fieldType === 'multiselect') {
    const nextValue = Array.isArray(value)
      ? [...new Set(value.map((entry) => String(entry || '').trim()).filter(Boolean))]
      : [];
    return nextValue.length ? nextValue : undefined;
  }

  if (fieldType === 'people') {
    const nextValue = Array.isArray(value)
      ? value.filter((entry) => getProfilePersonKey(entry))
      : [];
    return nextValue.length ? nextValue : undefined;
  }

  if (fieldType === 'annualdate') {
    return value && value.month && value.day ? value : undefined;
  }

  if (fieldType === 'date' || fieldType === 'datetime') {
    return value?.startDate ? value : undefined;
  }

  if (fieldType === 'daterange' || fieldType === 'datetimerange') {
    return value?.startDate ? value : undefined;
  }

  if (fieldType === 'boolean') {
    return Boolean(value);
  }

  if (fieldType === 'number') {
    if (value === '' || value == null) return undefined;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  if (fieldType === 'percentage') {
    if (value === '' || value == null) return undefined;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return undefined;
    return Math.max(0, Math.min(100, Math.round(numericValue)));
  }

  if (fieldType === 'money') {
    const parsed = parseCurrencyInputToCents(value, resolveFieldCurrencyCode(field));
    if (!parsed.ok || parsed.cents === '') return undefined;
    return parsed.cents;
  }

  return value;
}